import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  GrupoParametrosPlanilla,
  BaseCalculoPlanilla,
  FormulaFunction,
} from '../types/formula';
import { formulaService } from '../services/formulaService';

interface FormulaStore {
  grupos: GrupoParametrosPlanilla[];
  basesCalculo: BaseCalculoPlanilla[];
  funciones: FormulaFunction[];
  variablesDisponibles: string[];
  loading: boolean;
  error: string | null;
  dialogOpen: boolean;
  selectedBase: BaseCalculoPlanilla | null;

  fetchGrupos: () => Promise<void>;
  fetchBasesCalculo: () => Promise<void>;
  fetchFunciones: () => Promise<void>;
  fetchVariables: () => Promise<void>;
  saveBaseCalculo: (base: BaseCalculoPlanilla) => Promise<void>;
  deleteBaseCalculo: (id: number) => Promise<void>;
  openDialog: (base?: BaseCalculoPlanilla) => void;
  closeDialog: () => void;
  clearError: () => void;
}

export const useFormulaStore = create<FormulaStore>()(
  devtools(
    (set, get) => ({
      grupos: [],
      basesCalculo: [],
      funciones: [],
      variablesDisponibles: [],
      loading: false,
      error: null,
      dialogOpen: false,
      selectedBase: null,

      fetchGrupos: async () => {
        set({ loading: true, error: null });
        try {
          const grupos = await formulaService.getGrupos();
          set({ grupos, loading: false });
        } catch {
          set({ error: 'Error cargando grupos de parámetros', loading: false });
        }
      },

      fetchBasesCalculo: async () => {
        set({ loading: true, error: null });
        try {
          const basesCalculo = await formulaService.getBasesCalculo();
          set({ basesCalculo, loading: false });
        } catch {
          set({ error: 'Error cargando bases de cálculo', loading: false });
        }
      },

      fetchFunciones: async () => {
        try {
          const funciones = await formulaService.getFuncionesDisponibles();
          set({ funciones });
        } catch {
          // Non-critical
        }
      },

      fetchVariables: async () => {
        try {
          const variablesDisponibles = await formulaService.getVariablesDisponibles();
          set({ variablesDisponibles });
        } catch {
          // Non-critical
        }
      },

      saveBaseCalculo: async (base) => {
        set({ loading: true, error: null });
        try {
          await formulaService.saveBaseCalculo(base);
          await get().fetchBasesCalculo();
          set({ dialogOpen: false, selectedBase: null, loading: false });
        } catch {
          set({ error: 'Error guardando base de cálculo', loading: false });
        }
      },

      deleteBaseCalculo: async (id) => {
        set({ loading: true, error: null });
        try {
          await formulaService.deleteBaseCalculo(id);
          await get().fetchBasesCalculo();
          set({ loading: false });
        } catch {
          set({ error: 'Error eliminando base de cálculo', loading: false });
        }
      },

      openDialog: (base) => set({
        dialogOpen: true,
        selectedBase: base || null,
        error: null,
      }),

      closeDialog: () => set({
        dialogOpen: false,
        selectedBase: null,
        error: null,
      }),

      clearError: () => set({ error: null }),
    }),
    { name: 'FormulaStore' }
  )
);
